import { useId, useMemo, useState } from "react";

import { useMe } from "@/api";
import { Button } from "@/components/Button";
import { latestNews, markNewsSeen, newsSeen, type WhatsNewEntry } from "@/lib/whatsNew";

import styles from "./WhatsNewNote.module.css";

/** What `useWhatsNew` answers: the entry, whether it waits, and the way to put it away. */
export interface WhatsNew {
  /** The newest entry, if there is one — waiting or not. */
  news: WhatsNewEntry | null;
  /** Whether the signed-in person has yet to put it away. */
  waiting: boolean;
  putAway: () => void;
}

/**
 * „Neu in dieser Version" (Welle 1, idea 16): after a release, the app says
 * that something changed, the list one press away.
 *
 * Since UX-Gutachten 2 (B-5) a dot on the masthead's „?" — the pill „Neu"
 * beside the keys read like a status chip and said nothing of what it was,
 * one of five type faces at the masthead's right end. The dot says „there is
 * something new here"; „?" opens the keys' overview, and what is new stands
 * at its top until it is put away (`WhatsNewSection`). Afterwards it stays
 * readable at the overview's end, folded (`Help.tsx`).
 *
 * Once per person and entry, exactly as before: put away, it is remembered
 * under the person's id in this browser (`lib/whatsNew.ts`), and the next
 * entry comes back. Where storage refuses, it goes for this visit and returns
 * on the next — never a broken shell.
 */
export function useWhatsNew(): WhatsNew {
  const { data: me } = useMe();
  const news = latestNews();
  const userId = me?.id ?? null;
  // What was put away during this visit — the storage may not remember it.
  const [putAwayNow, setPutAwayNow] = useState<string | null>(null);
  const seen = useMemo(() => userId !== null && news !== null && newsSeen(userId, news.id), [userId, news]);
  const waiting = userId !== null && news !== null && !seen && putAwayNow !== `${userId} ${news.id}`;
  return {
    news,
    waiting,
    putAway: () => {
      if (userId === null || news === null) return;
      markNewsSeen(userId, news.id);
      setPutAwayNow(`${userId} ${news.id}`);
    },
  };
}

/**
 * What is new, at the top of the keys' overview while it waits: the lead, the
 * list behind „Was ist neu?", and the way to put it away. Not a live region:
 * it is not an event.
 */
export function WhatsNewSection({ news, onPutAway }: { news: WhatsNewEntry; onPutAway: () => void }) {
  const titleId = useId();
  const listId = useId();
  const [expanded, setExpanded] = useState(false);
  return (
    <section className={styles.section} aria-labelledby={titleId}>
      <h3 id={titleId} className={`label ${styles.title}`}>
        Neu in dieser Version
      </h3>
      <p className={styles.lead}>{news.lead}</p>
      <ul id={listId} className={styles.items} hidden={!expanded}>
        {news.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <div className={styles.actions}>
        <Button
          variant="leise"
          flush
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((was) => !was)}
        >
          {expanded ? "Weniger zeigen" : "Was ist neu?"}
        </Button>
        {/* Puts it away for good — the name says what, the word is short. */}
        <Button aria-label="„Neu in dieser Version“ ausblenden" onClick={onPutAway}>
          Ausblenden
        </Button>
      </div>
    </section>
  );
}
